import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase.config';
import { useMessagesStore } from '../stores';
import { getTenantStoreContext } from './userStoreCache';
import { cleanPhoneNumber } from '../utils/formatting';
import { isOnline } from './networkStatus';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Messages Listener Service
 * Listens to all conversations for the current tenant/store
 * and syncs them to the Zustand store
 */
export const initializeMessagesListener = () => {
  const { tenantId, storeId } = getTenantStoreContext();
  
  if (!tenantId || !storeId) {
    console.warn('⚠️ Cannot initialize messages listener: No tenant/store context');
    return () => {};
  }

  // Firestore persistent cache handles offline data automatically

  console.log(`👂 Initializing messages listener for tenant ${tenantId}, store ${storeId}`);
  
  // Path: tenants/{t}/stores/{s}/conversations
  const conversationsRef = collection(db, 'tenants', tenantId, 'stores', storeId, 'conversations');
  
  // Listen only to conversation list (metadata). Do NOT subscribe to every conversation's
  // messages here — that would open 40+ listeners. Messages are subscribed on-demand when
  // the user opens a thread (see Messages.js and subscribeToConversationMessages).
  const unsubConversations = onSnapshot(conversationsRef, (snapshot) => {
    useMessagesStore.getState().setInitialized(true);
  });
  
  return () => {
    unsubConversations();
    // Unsubscribe from any on-demand message listeners
    Object.keys(messageListeners).forEach(key => {
      messageListeners[key]();
      delete messageListeners[key];
    });
  };
};

const messageListeners = {};

/**
 * Subscribe to messages for one conversation (on-demand). Call this when the user
 * opens a thread. Unsubscribe when they leave via unsubscribeFromConversationMessages.
 */
export const subscribeToConversationMessages = (tenantId, storeId, docId) => {
  if (messageListeners[docId]) return; // Already listening

  const storeKey = cleanPhoneNumber(docId);

  const messagesRef = collection(db, 'tenants', tenantId, 'stores', storeId, 'conversations', docId, 'messages');
  const q = query(messagesRef);

  const unsub = onSnapshot(q, (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });

    messages.sort((a, b) => {
      const getTimestamp = (msg) => {
        const ts = msg.sentAt || msg.receivedAt || msg.createdAt;
        if (!ts) return 0;
        if (ts.seconds) return ts.seconds * 1000;
        if (ts instanceof Date) return ts.getTime();
        return ts;
      };
      return getTimestamp(a) - getTimestamp(b);
    });

    useMessagesStore.getState().setConversation(storeKey, {
      messages: messages
    });
  });

  messageListeners[docId] = unsub;
};

/**
 * Unsubscribe from messages for one conversation. Call when the user leaves the thread.
 */
export const unsubscribeFromConversationMessages = (docId) => {
  const unsub = messageListeners[docId];
  if (unsub) {
    unsub();
    delete messageListeners[docId];
  }
};

// localStorage caching functions removed - using Firestore persistent cache only


